import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Pagination,
  Snackbar,
  Alert,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { 
  getProducts, 
  createProduct, 
  updateProduct, 
  searchProducts,
  uploadProductsFile,
  downloadProductTemplate
} from '../../services/productapi';
import ProductFormModal from '../../components/products/ProductFormModal';
import FileUploadModalNew from '../../components/common/FileUploadModalNew';
import DataExportButton from '../../components/common/DataExportButton';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
    // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage,setRowsperPage] = useState(20);    const [totalCount, setTotalCount] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Helper function to fetch data with a specific status filter  // Using useCallback to memoize the fetch function to prevent unnecessary recreation
  const fetchDataWithStatus = async (pageNumber = null, status = null, isInitialLoad = false) => {
    // If there's already a fetch in progress and this is not the initial load, don't start another one
    if (loading && !isInitialLoad) {
      console.log('Fetch already in progress, skipping duplicate request');
      return;
    }
    
    try {
      setLoading(true);
      setIsSearchMode(false);
      setError(null); // Clear any previous errors
      
      // Use provided page number or current state
      const currentPage = pageNumber || page;
      // Use provided status or current statusFilter state
      const currentStatus = status !== null ? status : statusFilter;
      
      console.log(`Fetching products data for page ${currentPage}, status filter: ${currentStatus}`);
      
      // Prepare params including status filter
      const params = {
        page: currentPage,
        page_size: rowsPerPage, // Always include page_size parameter
      };
      
      // Add state filter if not 'all'
      if (currentStatus !== 'all') {
        params.state = currentStatus;
      }      // Add a cache buster to prevent browser/axios caching
      params._t = new Date().getTime();
      
      console.log('Sending API request with params:', params);
      const response = await getProducts(params);
      
      // Handle paginated response
      if (response.data.results) {
        console.log(`Received ${response.data.results.length} products, total: ${response.data.count}`);
        setProducts(response.data.results);
        setTotalCount(response.data.count);
        setRowsperPage(response.data.results.length);
      } else {
        // Fallback in case API doesn't return paginated response
        console.log(`Received ${response.data.length} products (non-paginated)`);
        setProducts(response.data);
        setTotalCount(response.data.length);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products data');
      // Initialize products as empty array when error occurs
      setProducts([]);
      setTotalCount(0);
      setLoading(false);
    }
  };
  const fetchData = async (pageNumber = null, isInitialLoad = false) => {
    // Always pass the current statusFilter to ensure server-side filtering is consistent
    return fetchDataWithStatus(pageNumber, statusFilter, isInitialLoad);
  };useEffect(() => {
    // Initial data load with active status filter applied
    // Using setTimeout to ensure that the component is fully mounted
    // This helps prevent duplicate fetches on initial load
    const initializationTimeout = setTimeout(() => {
      console.log('Running initial data fetch');
      fetchData(null, true); // Pass true to indicate this is the initial load
    }, 0);
    
    // Cleanup the timeout if component unmounts
    return () => clearTimeout(initializationTimeout);
  }, []);const handleRefresh = () => {
    // Reset error state before fetching data
    setError(null);
    setProducts([]); // Clear products to avoid showing stale data
    setTotalCount(0);
    fetchData(); // Fetch fresh data
  };
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // Always reset to first page when searching
    const newPage = 1;
    setPage(newPage);
    
    // If the search term is empty, revert to regular data fetch
    if (!value.trim()) {
      fetchData(newPage);
      return;
    }
    
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      performSearch(value, newPage);
    }, 500);
      return () => clearTimeout(timeoutId);
  };
  
  const performSearch = async (query, pageNumber = null, status = null) => {
    if (!query.trim()) return;
    
    try {
      setLoading(true);
      setIsSearchMode(true);
      
      // Use provided page number or current state
      const currentPage = pageNumber || page;
      // Use provided status or current status filter
      const statusValue = status !== null ? status : statusFilter;
        console.log(`Searching products for "${query}" on page ${currentPage}, status filter: ${statusValue}`);
      
      // Call searchProducts with all parameters including status filter
      const response = await searchProducts(query, currentPage, rowsPerPage, statusValue);
      
      // Handle paginated search results
      if (response.data.results) {
        console.log(`Search returned ${response.data.results.length} products, total: ${response.data.count}`);
        setProducts(response.data.results);
        setTotalCount(response.data.count);
      } else {
        console.log(`Search returned ${response.data.length} products (non-paginated)`);
        setProducts(response.data);        setTotalCount(response.data.length);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error searching products:', err);
      setError('Search failed. Please try again.');
      setProducts([]); // Clear products to avoid showing stale data
      setTotalCount(0);
      setLoading(false);
    }
  };
  const handleStatusChange = (event) => {
    const newStatusFilter = event.target.value;
    setStatusFilter(newStatusFilter);
    
    // Reset to first page when filtering
    const newPage = 1;
    setPage(newPage);
    
    // If in search mode and we have a search term, search with the new status filter
    if (isSearchMode && searchTerm) {
      // Use server-side filtering for search results too
      performSearch(searchTerm, newPage, newStatusFilter);    } else {
      // Otherwise, just fetch the new page of data with the new status filter
      // We need to create a custom call that uses the new status filter
      // since the state won't be updated yet
      fetchDataWithStatus(newPage, newStatusFilter);
    }
  };
  
  const handleAddProduct = () => {
    setCurrentProduct(null); // Reset current product for adding a new one
    setModalOpen(true);
  };
    const handleEditProduct = (product) => {
    console.log('Editing product with data:', product);
    // Make sure product data is complete, especially for category field
    console.log('Product category ID:', product.category, typeof product.category);
    setCurrentProduct(product);
    setModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentProduct(null);
  };
  
  const handleSaveProduct = async (formData, mode) => {
    try {
      setSaveLoading(true);
      
      // Log what we're sending to the API
      console.log(`${mode === 'add' ? 'Creating' : 'Updating'} product with data:`, formData);
      
      let response;
      if (mode === 'add') {
        response = await createProduct(formData);
        setNotification({
          open: true,
          message: 'Product created successfully!',
          severity: 'success'
        });
      } else {
        response = await updateProduct(currentProduct.id, formData);
        setNotification({
          open: true,
          message: 'Product updated successfully!',
          severity: 'success'
        });
      }
      
      // Close modal and refresh data
      setModalOpen(false);
      setCurrentProduct(null);
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      // Try to extract a more helpful error message from the API response
      let errorMessage = 'Failed to save product';
      
      if (error.response && error.response.data) {
        // DRF often returns errors as an object with field names as keys
        if (typeof error.response.data === 'object') {
          const errorDetails = Object.entries(error.response.data)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          
          if (errorDetails) {
            errorMessage = errorDetails;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          }
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: `Error: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setSaveLoading(false);
    }
  };
    const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };
  
  const handleOpenUploadModal = () => {
    setUploadModalOpen(true);
  };
  
  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };
  
  const handleUploadSuccess = () => {
    setNotification({
      open: true,
      message: 'Products uploaded successfully!',
      severity: 'success'
    });
      // Refresh the product list after successful upload
    fetchData();
  };
  
  // No client-side filtering needed as we're using server-side filtering
  
  // Pagination calculations - using totalCount from API for server-side pagination
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  
  // With server-side pagination and filtering, we don't need to modify the data,
  // as the API already returns the correct page of data with the correct filters
  const paginatedProducts = products;  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    
    // If we're in search mode, we need to search with the new page and current status filter
    if (isSearchMode && searchTerm) {
      performSearch(searchTerm, newPage, statusFilter);
    } else {
      // Otherwise just fetch the new page of data with current status filter
      fetchData(newPage);
    }
  };

  // Format date to a readable format
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ padding: { xs: 0, md: 3 } }}>
      <Breadcrumbs items={[
        { label: 'Inventory Management', href: '/inventory' },
        { label: 'Products' }
      ]} />    
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 500, color: '#333' }}>
          Products Management
        </Typography>
        
        <Stack direction="row" spacing={2}>
          <Button 
            variant="outlined" 
            startIcon={<CloudUploadIcon />}
            sx={{ textTransform: 'none' }}
            onClick={handleOpenUploadModal}
          >
            Upload Products
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            sx={{ 
              bgcolor: '#3f51b5', 
              '&:hover': { bgcolor: '#303f9f' },
              textTransform: 'none'
            }}
            onClick={handleAddProduct}
          >
            Add New Product
          </Button>
        </Stack>
      </Box>
      
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }} elevation={0}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search products..."
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusChange}
            size="small"
            sx={{ width: 150 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="discontinued">Discontinued</MenuItem>
          </TextField>
            <Tooltip title="Refresh products data">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
            <DataExportButton 
            modelName="Product" 
            tooltipTitle="Export products data"
          />
          
          <Tooltip title="Filter options">
            <IconButton>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }} elevation={0}>
          <Typography color="error">{error}</Typography>
          <Button 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh}
            sx={{ mt: 2 }}
            variant="outlined"
          >
            Try Again
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                <TableCell>Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Category</TableCell>
                <TableCell >Location</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Materials</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>MRP</TableCell>
                <TableCell >Cost Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No products found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedProducts.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{product.name}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {product.category_name || '-'}
                    </TableCell>
                    <TableCell  >
                      {product.location_name || '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {product.material || '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      ₹{product.mrp}
                    </TableCell>
                    <TableCell >
                      ₹{product.cost_price}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.state}
                        size="small"
                        sx={{
                          bgcolor:
                            product.state === 'active'
                              ? '#e3f2fd'
                              : product.state === 'inactive'
                              ? '#ffebee'
                              : '#f5f5f5',
                          color:
                            product.state === 'active'
                              ? '#1565c0'
                              : product.state === 'inactive'
                              ? '#c62828'
                              : '#616161',
                          borderRadius: '16px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {formatDate(product.created_at)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Variants">
                          <IconButton size="small" color="info">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Product">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditProduct(product)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>        
        </TableContainer>
      )}
        {!loading && !error && products.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>          
        <Pagination 
            count={totalPages} 
            page={page}
            onChange={(e, newPage) => {
              console.log(`Pagination clicked: changing to page ${newPage}`);
              handleChangePage(e, newPage);
            }}
            color="primary" 
            size="medium" 
            showFirstButton
            showLastButton
          />
        </Box>
      )}
      
      {/* Product Form Modal */}
      <ProductFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveProduct}
        product={currentProduct}
        isLoading={saveLoading}
      />
        {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
        {/* Product Upload Modal */}
      <FileUploadModalNew
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        onUploadSuccess={handleUploadSuccess}
        app="product"
        table="products"
        title="Upload"
        uploadFunction={(formData) => {
          // For products app, we need to specify the 'type' parameter
          formData.append('type', 'products');
          return uploadProductsFile(formData);
        }}
        downloadTemplateFunction={(fmt) => downloadProductTemplate('products', fmt)}
      />
    </Box>
  );
};

export default ProductsPage;
